import React from 'react';
import { PureComponent } from '../../../../dispatcher/modules';
import {
  NumericTextBox as KendoNumericTextBox,
  NumericTextBoxBlurEvent,
  NumericTextBoxChangeEvent,
  NumericTextBoxFocusEvent
} from '@progress/kendo-react-inputs';
import * as _ from 'lodash';
import { classNames } from '@progress/kendo-react-common';
import { PUZZLE_FIELD_STATE } from '../Types';
import { PUZZLE_NUMERIC_FIELD_PROPS } from './Types';
import Field from '../../../layout/field/Field';
import { Label } from '@progress/kendo-react-labels';
import {
  ABSTRACT_FIELD_OMITTED_PROPS,
  FIELD_OMITTED_PROPS
} from '../../../../utility';

/**
 * For the layout and design purposes the entire Kendo NumericTextBox needs to be wrapped with this cmp.
 * Cmp handles all events that occur and flags component state represented with CSS classes
 * @author Stevan Stojanovic
 */
class PuzzleNumericField extends PureComponent<PUZZLE_NUMERIC_FIELD_PROPS, PUZZLE_FIELD_STATE> {
  numericTextBoxRef: any = null;
  numericTextBoxID: any = null;

  static defaultProps = {
    size: 'medium'
  };

  componentDidMount() {
    this.numericTextBoxID = this.props.id ? this.props.id : this.numericTextBoxRef?.element?.id;
  }

  private events = {
    onFocus: (event: NumericTextBoxFocusEvent) => this.triggerEvent('onFocus', event),
    onBlur: (event: NumericTextBoxBlurEvent) => this.triggerEvent('onBlur', event),
    onChange: (event: NumericTextBoxChangeEvent) => this.triggerEvent('onChange', event)
  };

  render() {
    const {
      asField,
      className = '',
      valid,
      value,
      ariaDescribedBy,
      errorTxt,
      dataCy,
      disabled,
      size,
      label,
      labelClassName = '',
      withValidation,
      fieldClassName = '',
      fieldMandatory,
      fieldWithAdditionalEl
    } = this.props;

    const asFieldProps = {
      fieldClassName,
      fieldMandatory,
      fieldWithAdditionalEl
    };

    const numericTextBoxOmittedProps = ['dataCy'].concat(FIELD_OMITTED_PROPS, ABSTRACT_FIELD_OMITTED_PROPS);
    const numericTextBoxProps = _.omit(this.props, numericTextBoxOmittedProps);

    const fieldCls = classNames(
      {
        [className]: className,
        'k-medium': size === 'medium',
        'k-state-empty': !value,
        'k-state-invalid': valid === false,
        'k-state-disabled': disabled
      }
    );

    const content = (
      <div
        className={fieldCls}
        data-cy={dataCy ? `${dataCy}-wrapper` : undefined}
      >
        {label && <Label
          editorId={this.numericTextBoxID}
          className={classNames({
            [labelClassName]: labelClassName,
            'k-mandatory': !asField && fieldMandatory
          })}
        >
          {label}
        </Label>}
        <KendoNumericTextBox
          id={this.numericTextBoxID}
          // data-cy={dataCy ? dataCy : 'puzzle-numeric'}
          data-cy={'puzzle-numeric'}
          ref={(numericTextBox: any) => this.numericTextBoxRef = numericTextBox}
          {...numericTextBoxProps}
          {...this.events}
        />
        {withValidation && <div
          className={'tp-validation-error-message'}
          data-cy={dataCy ? `${dataCy}-error-message` : undefined}
          id={ariaDescribedBy}
        >
          {errorTxt}
        </div>}
      </div>
    );

    if (asField) {
      return (
        <Field {...asFieldProps}>
          {content}
        </Field>
      );
    }

    return content;
  }

  /**
   * Helper method for triggering events outside the cmp itself
   **/
  private triggerEvent(eventType: string, event: any) {
    if (this.props[eventType]) {
      this.props[eventType].call(undefined, {
        ...event,
        target: this.numericTextBoxRef
      });
    }
  }
}

export default PuzzleNumericField;
